import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Building2, MapPin, Plus, Pencil, Trash2, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';

interface Branch {
  id: string;
  name: string;
  city: string;
  address: string;
  phone?: string;
  employees?: number;
  is_headquarters?: boolean;
}

interface BranchSelectorProps {
  companyName: string;
  companies: any[];
  selectedBranch: string;
  onBranchSelect: (branch: string) => void;
  onCompanyUpdate?: (companies: any[]) => void;
  variant?: 'default' | 'compact';
  showLabel?: boolean;
  showTextInput?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  invalid?: boolean;
}

export function BranchSelector({
  companyName,
  companies,
  selectedBranch,
  onBranchSelect,
  onCompanyUpdate,
  variant = 'default',
  showLabel = true,
  showTextInput = true,
  inputRef,
  invalid = false,
}: BranchSelectorProps) {
  const [isManageBranchOpen, setIsManageBranchOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [isAddingBranch, setIsAddingBranch] = useState(false);
  const [newBranch, setNewBranch] = useState<Partial<Branch>>({
    name: '',
    city: '',
    address: '',
    phone: '',
    employees: undefined,
    is_headquarters: false,
  });

  // Find the selected company
  const selectedCompany = useMemo(() => {
    return companies.find((c) => c.name === companyName);
  }, [companies, companyName]);

  // Get branches for the selected company
  const branches: Branch[] = useMemo(() => {
    return selectedCompany?.branches || [];
  }, [selectedCompany]);

  const companyAddress = useMemo(() => {
    const c: any = selectedCompany || {};
    const partsRaw = [
      String(c?.location || '').trim(),
      String(c?.address || '').trim(),
      String(c?.city || '').trim(),
      String(c?.province || c?.state || '').trim(),
      String(c?.country || '').trim(),
    ].filter(Boolean);
    const tokens: string[] = [];
    const seen = new Set<string>();
    partsRaw
      .flatMap(p => String(p).split(/[,-]/).map(s => s.trim()).filter(Boolean))
      .forEach(tok => { const k = tok.toLowerCase(); if (!seen.has(k)) { seen.add(k); tokens.push(tok); } });
    const joined = tokens.join(', ');
    return joined || String(c?.location || '').trim();
  }, [selectedCompany]);

  const handleAddBranch = async () => {
    if (!newBranch.name || !newBranch.city) {
      toast.error('Branch name and city are required');
      return;
    }

    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }

    try {
      const branchToAdd: Branch = {
        id: Math.random().toString(36).slice(2, 11),
        name: newBranch.name,
        city: newBranch.city,
        address: newBranch.address || '',
        phone: newBranch.phone || '',
        employees: newBranch.employees || 0,
        is_headquarters: newBranch.is_headquarters || false,
      };

      const updatedBranches = [...branches, branchToAdd];

      // Update company with new branch
      const updatedCompany = await apiClient.updateCompanyByAdmin(selectedCompany.id, {
        branches: updatedBranches,
      });

      // Update local state
      if (onCompanyUpdate) {
        const updatedCompanies = companies.map((c) =>
          c.id === selectedCompany.id ? { ...c, branches: updatedBranches } : c
        );
        onCompanyUpdate(updatedCompanies);
      }

      toast.success('Branch added successfully');
      setNewBranch({
        name: '',
        city: '',
        address: '',
        phone: '',
        employees: undefined,
        is_headquarters: false,
      });
      setIsAddingBranch(false);
    } catch (error) {
      console.error('Failed to add branch:', error);
      toast.error('Failed to add branch');
    }
  };

  const handleUpdateBranch = async () => {
    if (!editingBranch || !editingBranch.name || !editingBranch.city) {
      toast.error('Branch name and city are required');
      return;
    }

    if (!selectedCompany) {
      toast.error('Company not found');
      return;
    }

    try {
      const updatedBranches = branches.map((b) =>
        b.id === editingBranch.id ? editingBranch : b
      );

      await apiClient.updateCompanyByAdmin(selectedCompany.id, {
        branches: updatedBranches,
      });

      if (onCompanyUpdate) {
        const updatedCompanies = companies.map((c) =>
          c.id === selectedCompany.id ? { ...c, branches: updatedBranches } : c
        );
        onCompanyUpdate(updatedCompanies);
      }

      toast.success('Branch updated successfully');
      setEditingBranch(null);
    } catch (error) {
      console.error('Failed to update branch:', error);
      toast.error('Failed to update branch');
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (!selectedCompany) return;

    try {
      const updatedBranches = branches.filter((b) => b.id !== branchId);

      await apiClient.updateCompanyByAdmin(selectedCompany.id, {
        branches: updatedBranches,
      });

      if (onCompanyUpdate) {
        const updatedCompanies = companies.map((c) =>
          c.id === selectedCompany.id ? { ...c, branches: updatedBranches } : c
        );
        onCompanyUpdate(updatedCompanies);
      }

      // Clear selection if deleted branch was selected
      if (selectedBranch.includes(branches.find((b) => b.id === branchId)?.city || '')) {
        onBranchSelect('');
      }

      toast.success('Branch deleted successfully');
    } catch (error) {
      console.error('Failed to delete branch:', error);
      toast.error('Failed to delete branch');
    }
  };

  const formatBranchLocation = (branch: Branch) => {
    const city = String(branch.city || '').trim();
    const addr = String(branch.address || '').trim();
    const showAddr = !!addr && addr.toLowerCase() !== city.toLowerCase();
    return `${branch.name} - ${city}${showAddr ? ', ' + addr : ''}`;
  };

  const selectTriggerBase = variant === 'compact'
    ? 'w-full h-10 px-3 bg-transparent border-0 outline-none shadow-none rounded-none text-sm'
    : 'w-full h-10 px-3 bg-white border border-gray-200 rounded-md shadow-none hover:bg-transparent focus:outline-none focus:ring-0';
  const invalidCls = invalid ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : '';
  const selectTriggerCls = `${selectTriggerBase} ${invalidCls}`;

  return (
    <div className="space-y-1">
      {showLabel && (
        <div className="flex items-center justify-between">
          <Label htmlFor="branch-select" className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            Location / Branch
          </Label>
          {selectedCompany && (
            <span className="text-xs text-gray-500">
              {branches.length} {branches.length === 1 ? 'branch' : 'branches'} available
            </span>
          )}
        </div>
      )}

      {variant === 'compact' ? (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Select value={selectedBranch} onValueChange={onBranchSelect}>
              <SelectTrigger
                className={selectTriggerCls}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onKeyDownCapture={(e) => e.stopPropagation()}
                onKeyUpCapture={(e) => e.stopPropagation()}
                onInputCapture={(e) => e.stopPropagation()}
              >
                <SelectValue placeholder={companyName ? 'Select branch location' : 'Select a company first'} />
              </SelectTrigger>
              <SelectContent className="z-[10002] shadow-none border border-gray-200 rounded-md overflow-hidden p-0">
                {companyAddress && (
                  <SelectItem value={companyAddress} className="text-sm cursor-pointer">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      <span>Company Location — {companyAddress}</span>
                    </div>
                  </SelectItem>
                )}
                {branches.length === 0 && !companyAddress && (
                  <div className="px-2 py-4 text-center text-sm text-gray-500">
                    <Building2 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    No branches available for this company
                  </div>
                )}
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={formatBranchLocation(branch)} className="text-sm cursor-pointer">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      <span>{formatBranchLocation(branch)}</span>
                      {branch.is_headquarters && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">HQ</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsManageBranchOpen(true)}
            disabled={!companyName}
            className="gap-2 h-10"
          >
            <Building2 className="h-4 w-4" />
            Manage
          </Button>
        </div>
      ) : (
        <div className="w-full">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              {showTextInput ? (
                <Input
                  ref={inputRef}
                  value={selectedBranch}
                  onChange={(e) => onBranchSelect(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onKeyDownCapture={(e) => e.stopPropagation()}
                  onKeyUpCapture={(e) => e.stopPropagation()}
                  onInputCapture={(e) => e.stopPropagation()}
                  placeholder={companyName ? 'Enter branch location' : 'Select a company first'}
                  className={`h-10 text-sm px-3 bg-white border border-gray-200 rounded-md ${invalidCls}`}
                />
              ) : (
                <Select value={selectedBranch} onValueChange={onBranchSelect}>
                  <SelectTrigger
                    className={selectTriggerCls}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onKeyDownCapture={(e) => e.stopPropagation()}
                    onKeyUpCapture={(e) => e.stopPropagation()}
                    onInputCapture={(e) => e.stopPropagation()}
                  >
                    <SelectValue placeholder={companyName ? 'Select branch location' : 'Select a company first'} />
                  </SelectTrigger>
                  <SelectContent className="z-[10002] shadow-none border border-gray-200 rounded-md overflow-hidden p-0">
                    {companyAddress && (
                      <SelectItem value={companyAddress} className="text-sm cursor-pointer">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          <span>Company Location — {companyAddress}</span>
                        </div>
                      </SelectItem>
                    )}
                    {branches.length === 0 && !companyAddress && (
                      <div className="px-2 py-4 text-center text-sm text-gray-500">
                        <Building2 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                        No branches available for this company
                      </div>
                    )}
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={formatBranchLocation(branch)} className="text-sm cursor-pointer">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          <span>{formatBranchLocation(branch)}</span>
                          {branch.is_headquarters && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">HQ</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsManageBranchOpen(true)}
              disabled={!companyName}
              className="gap-2 h-10"
            >
              <Building2 className="h-4 w-4" />
              Manage
            </Button>
          </div>
        </div>
      )}

      {/* Branch Management Dialog */}
      <Dialog open={isManageBranchOpen} onOpenChange={setIsManageBranchOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Manage Branches - {companyName}
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove company branch locations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Existing Branches List */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">
                Existing Branches ({branches.length})
              </h4>
              {branches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No branches added yet</p>
                  <p className="text-sm">Click "Add New Branch" to get started</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {branches.map((branch) => (
                    <div
                      key={branch.id}
                      className="p-4  bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold text-gray-800">{branch.name}</h5>
                            {branch.is_headquarters && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5  font-medium">
                                Headquarters
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>{branch.city}{branch.address && ` - ${branch.address}`}</span>
                            </div>
                            {branch.phone && (
                              <div className="text-xs text-gray-500">📞 {branch.phone}</div>
                            )}
                            {branch.employees !== undefined && branch.employees > 0 && (
                              <div className="text-xs text-gray-500">👥 {branch.employees} employees</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingBranch(branch)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBranch(branch.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Branch Button */}
            {!isAddingBranch && !editingBranch && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddingBranch(true)}
                className="w-full gap-2 border-dashed border-2"
              >
                <Plus className="h-4 w-4" />
                Add New Branch
              </Button>
            )}

            {/* Add New Branch Form */}
            {isAddingBranch && (
              <div className="p-4 border-2 border-blue-200  bg-blue-50/50 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800">Add New Branch</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAddingBranch(false);
                      setNewBranch({
                        name: '',
                        city: '',
                        address: '',
                        phone: '',
                        employees: undefined,
                        is_headquarters: false,
                      });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Branch Name *</Label>
                    <Input
                      value={newBranch.name || ''}
                      onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
                      placeholder="e.g., Main Office"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={newBranch.city || ''}
                      onChange={(e) => setNewBranch({ ...newBranch, city: e.target.value })}
                      placeholder="e.g., Kathmandu"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Full Address</Label>
                    <Input
                      value={newBranch.address || ''}
                      onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })}
                      placeholder="e.g., New Road, Kathmandu"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={newBranch.phone || ''}
                      onChange={(e) => setNewBranch({ ...newBranch, phone: e.target.value })}
                      placeholder="e.g., +977-1-4234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Employees</Label>
                    <Input
                      type="number"
                      value={newBranch.employees || ''}
                      onChange={(e) =>
                        setNewBranch({ ...newBranch, employees: e.target.value ? Number(e.target.value) : undefined })
                      }
                      placeholder="e.g., 50"
                      min="0"
                    />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <input
                      type="checkbox"
                      id="new-hq"
                      checked={newBranch.is_headquarters || false}
                      onChange={(e) => setNewBranch({ ...newBranch, is_headquarters: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="new-hq" className="cursor-pointer">
                      Mark as Headquarters
                    </Label>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsAddingBranch(false);
                      setNewBranch({
                        name: '',
                        city: '',
                        address: '',
                        phone: '',
                        employees: undefined,
                        is_headquarters: false,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleAddBranch}>
                    Add Branch
                  </Button>
                </div>
              </div>
            )}

            {/* Edit Branch Form */}
            {editingBranch && (
              <div className="p-4 border-2 border-amber-200  bg-amber-50/50 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800">Edit Branch</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingBranch(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Branch Name *</Label>
                    <Input
                      value={editingBranch.name}
                      onChange={(e) => setEditingBranch({ ...editingBranch, name: e.target.value })}
                      placeholder="e.g., Main Office"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={editingBranch.city}
                      onChange={(e) => setEditingBranch({ ...editingBranch, city: e.target.value })}
                      placeholder="e.g., Kathmandu"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Full Address</Label>
                    <Input
                      value={editingBranch.address}
                      onChange={(e) => setEditingBranch({ ...editingBranch, address: e.target.value })}
                      placeholder="e.g., New Road, Kathmandu"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={editingBranch.phone || ''}
                      onChange={(e) => setEditingBranch({ ...editingBranch, phone: e.target.value })}
                      placeholder="e.g., +977-1-4234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Employees</Label>
                    <Input
                      type="number"
                      value={editingBranch.employees || ''}
                      onChange={(e) =>
                        setEditingBranch({ ...editingBranch, employees: e.target.value ? Number(e.target.value) : undefined })
                      }
                      placeholder="e.g., 50"
                      min="0"
                    />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <input
                      type="checkbox"
                      id="edit-hq"
                      checked={editingBranch.is_headquarters || false}
                      onChange={(e) => setEditingBranch({ ...editingBranch, is_headquarters: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit-hq" className="cursor-pointer">
                      Mark as Headquarters
                    </Label>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingBranch(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleUpdateBranch}>
                    Update Branch
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageBranchOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
